import { defineStore } from 'pinia';
import { useNuxtApp, useRouter } from 'nuxt/app';
import { computed, ref } from 'vue';
import { setErrorNotification } from '~~/common/utils/helper';
import { commitAlcoholAlertModal, commitAlcoholConfirmModal } from '~~/common/utils/alcoholManagement';
import { M_DELIVERY_METHOD } from '~~/common/config/Modal';
import { SEO_MODULE_CATEGORY } from '~~/common/config/SeoConfig';
import otqs from '~~/common/utils/objectToQueryString';
import { extractResourceFromPath, getSubcategoryId } from '~~/common/utils/urlResource';
import { AW_AX_CACHE_SEGMENTATION } from '~~/common/plugins/axios/awAxCache';
import { PUSH_CATEGORY_VIEW } from '~~/common/plugins/aw-analytics';
import { useNotificationStore } from '~~/common/stores/notification';
import { useModalStore } from '~~/common/stores/modal';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useSeoStore } from '~~/common/stores/seo';
import { useUserStore } from '~~/common/stores/user';
import { useAuthenticationStore } from '~~/common/stores/authentication';
import { useAwCacheKeyStore } from '~~/common/stores/awCacheKey';
import type { DeepPartial, ResponseData, ResponseDataStrict, Schema } from '~~/common/types/api';
import { isApiError } from '~~/common/utils/apiError';

type Logger = ReturnType<typeof useNuxtApp>['$logger'];

// Recursively strips `readonly` so the store can mutate product data in place to drive reactivity.
type DeepWritable<T> =
  T extends (infer U)[] ? DeepWritable<U>[]
    : T extends object ? { -readonly [K in keyof T]: DeepWritable<T[K]> }
      : T;

// A product list item: because of updates due to cache, it should be writable
export type Product = DeepWritable<DeepPartial<Schema<'ProductModel'>>> & { feMeta?: { isCached: boolean } };

type ProductsResponse = ResponseData<'/api/v2/products', 'get'>;

type SortValue = NonNullable<ProductsResponse['sortValues']>[number];

//todo use backend API types after backend API swagger is updated with the correct types
// be documents the `categorySelection` filter's values as FilterValueModel-default (`name`, `value`,
// `iconName`), but GET /api/v2/products actually returns a category tree in
// `filters[].values[].children` (verified against the live backend: `children` is present on every
// level but the deepest, and only on that filter).
type ProductFilterValue = NonNullable<NonNullable<ProductsResponse['filters']>[number]['values']>[number] & {
  children?: ProductFilterValue[] | null;
};

type ProductFilter = Omit<NonNullable<ProductsResponse['filters']>[number], 'values'> & {
  values?: ProductFilterValue[];
};

type SimilarProductFiltersResponse = Schema<'SimilarProductFiltersModel.SimilarProductResponseModel'>;
type SimilarProductFilter = SimilarProductFiltersResponse['filters'][number];

type ProductsResponseData = Omit<ProductsResponse, 'results' | 'filters'> & {
  results?: Product[];
  filters?: ProductFilter[];
};

export type FilterValue = string | number;
// Most filters store a scalar or a flat id list. The range filter (price) is the
// exception: it stores a nested [[min, max]] pair, read by the RangeFilter consumers
// as filterBy[id][0][0]/[0][1]. FilterValue[][] is therefore part of the union.
type FilterValueDeep = FilterValue | FilterValue[] | FilterValue[][];
type FilterParams = Record<string, FilterValueDeep>;

export interface ProductQueryParams {
  isCached?: boolean;
  page?: number;
  itemsPerPage?: number | null;
  categoryId?: string | number;
  boutiqueId?: string | number;
  subCategoryId?: string | number;
  // Multi-value, hierarchy-aware category filter: any level can be passed and the
  // backend applies an inclusive (parent covers all descendants) OR filter.
  categories?: number[];
  flagId?: string | number;
  listId?: string | number;
  listName?: string;
  substring?: string;
  multipleSubstring?: string[];
  //todo use backend API types after backend API swagger is updated with the correct types
  // be does not document `auchanCodes`, but GET /api/v2/products accepts it (filters by Auchan
  // product codes, verified against the live backend).
  auchanCodes?: Array<string | number>;
  sortKey?: string | null;
  sortBy?: string | null;
  filterParams?: FilterParams;
  allPreviouslyOrdered?: boolean;
  recommendationCarouselId?: string | number;
  discountedOnly?: boolean;
  loyaltyOffer?: string;
  //todo use backend API types after backend API swagger is updated with the correct types
  // be does not document `productIds`, but GET /api/v2/products accepts it (filters to the given
  // product ids, used by the order-again basket refresh).
  productIds?: Array<number | undefined>;
}

interface ProductListHistory {
  products: Product[];
  location: string;
  productId: number | string;
  currentPage: number;
  returnEnabled: boolean;
}

// Strict: the store maps the response into `UnCachedProduct` instances, whose fields are all
// required, and the swagger declares every one of them as required too.
type CartButtonsResponse = ResponseDataStrict<'/api/v2/products/cart_buttons_display_status', 'get'>;

type UnCachedProductData = CartButtonsResponse['list'][number];

export class UnCachedProduct {
  productId: number;
  shoppingListsContain: string[];
  status: string;
  isLoyaltyPriceValid: boolean;

  constructor ({
    productId,
    shoppingListsContain,
    status,
    isLoyaltyPriceValid,
  }: UnCachedProductData) {
    this.productId = productId;
    this.shoppingListsContain = shoppingListsContain;
    this.status = status;
    this.isLoyaltyPriceValid = isLoyaltyPriceValid;
  }
}

export function updateProdWithUnCachedData ({
  productsToMutate,
  payload,
  $logger,
}: {
  productsToMutate: Product[];
  payload: Record<string, UnCachedProduct>;
  $logger: Logger;
}) {
  const idsToMutate = new Set(Object.keys(payload));
  productsToMutate
    .filter(prod => idsToMutate.has(String(prod.id)))
    .forEach((prod) => {
      try {
        const payloadProd = payload[String(prod.id)];
        if (!(payloadProd instanceof UnCachedProduct)) {
          $logger.warn('payloadProd should be UnCachedProduct, continuing but we may fail.');
        }
        const variant = prod.selectedVariant;
        if (!payloadProd || !variant) {
          return;
        }
        if (payloadProd.shoppingListsContain !== variant.shoppingListsContain) {
          variant.shoppingListsContain = payloadProd.shoppingListsContain;
        }
        if (variant.cartInfo && payloadProd.status !== variant.cartInfo.availability) {
          variant.cartInfo.availability = payloadProd.status;
        }
        if (payloadProd.isLoyaltyPriceValid !== variant.isLoyaltyPriceValid) {
          variant.isLoyaltyPriceValid = payloadProd.isLoyaltyPriceValid;
        }
        if (prod.feMeta?.isCached === true) {
          prod.feMeta.isCached = false;
        }
      } catch (err) {
        $logger.error(err);
      }
    })
  ;
}

interface ThrottledRefresh {
  id: ReturnType<typeof setTimeout> | null;
  promise: Promise<void> | null;
  resolve: (() => void) | null;
}

const throttledRefreshObject: ThrottledRefresh = {
  id: null,
  promise: null,
  resolve: null,
};

export const useProductsStore = defineStore('products', () => {
  const nuxtApp = useNuxtApp();
  const router = useRouter();
  const notificationStore = useNotificationStore();
  const userStore = useUserStore();
  const seoStore = useSeoStore();
  const modalStore = useModalStore();
  const authenticationStore = useAuthenticationStore();
  const awCacheKeyStore = useAwCacheKeyStore();

  // products from result
  const products = ref<Product[]>([]);
  const productsCategories = ref<unknown[] | null>(null);

  const containsAgeRestrictedProduct = ref(false);
  // pagination data
  const currentPage = ref(1);
  const itemCount = ref<number | null>(null);
  const pageCount = ref<number | null>(null);
  const itemsPerPage = ref<number | null>(null);

  // order and filter data
  const sortValues = ref<SortValue[] | null>(null);
  const filters = ref<ProductFilter[]>([]);
  const sortKey = ref<string | null>(null);
  const sortBy = ref<string | null>(null);
  const filterBy = ref<FilterParams>({});
  const searchText = ref<string[]>([]);
  const noResultsFor = ref<NonNullable<ProductsResponse['noResultsFor']>>([]);
  const productListHistoryInformation = ref<ProductListHistory | null>(null);
  const flagFilter = ref<number | null>(null);
  /**
   * product should be refreshed on state changes. it's used as an
   * 'event' state variable. It's value is not relevant.
   * */
  const refreshProducts = ref(0);
  const alcoholProductIsLoading = ref(false);

  // visited page history
  const visitedPages = ref<unknown[]>([]);
  const lastFilterableResource = ref<unknown>(null);

  // checkboxFilter helper
  const filterKeyword = ref<Record<string, unknown>>({});

  // Filtering
  const filterPanelVisible = ref(false);

  function pushProducts (productsArg: Product[]) {
    products.value.push(...productsArg);
  }

  function setCategoryPageAttrs (attrs: {
    containsAgeRestrictedProduct?: boolean;
    currentPage?: number;
    itemCount?: number;
    pageCount?: number;
    itemsPerPage?: number | null;
    sortValues?: SortValue[];
    filters?: ProductFilter[];
    noResultsFor?: ProductsResponse['noResultsFor'];
    sortKey?: string | null;
    sortBy?: string | null;
    productsCategories?: unknown[];
  }) {
    containsAgeRestrictedProduct.value = Boolean(attrs?.containsAgeRestrictedProduct);
    currentPage.value = attrs?.currentPage || currentPage?.value;
    if (attrs?.itemCount || attrs?.itemCount === 0) {
      itemCount.value = attrs?.itemCount;
    }
    pageCount.value = attrs?.pageCount || pageCount.value;
    itemsPerPage.value = attrs?.itemsPerPage || itemsPerPage.value;
    sortValues.value = attrs?.sortValues || sortValues.value;
    filters.value = attrs?.filters || filters.value;
    noResultsFor.value = attrs?.noResultsFor || noResultsFor.value;
    sortKey.value = attrs?.sortKey ?? null;
    sortBy.value = attrs?.sortBy ?? null;
    productsCategories.value = attrs?.productsCategories ?? null;
  }

  function setSearchText (searchTextArg: string[] | string) {
    const st = Array.isArray(searchTextArg) ? searchTextArg : searchTextArg.split('\n');
    searchText.value = Array.from(new Set(st.filter(f => f.trim())));
  }

  function clearFilterAndSort () {
    filters.value = [];
    sortKey.value = null;
    sortBy.value = null;
    filterBy.value = {};
    flagFilter.value = null;
  }

  function setActiveFilter (filter: Record<string, FilterValue | FilterValue[]>) {
    Object.entries(filter).forEach(([key, value]) => {
      const current = filterBy.value[key];
      // Toggle within an existing flat id list (categories/brand). Range filters pass
      // an array value and never reach this branch, so `current` here is FilterValue[].
      if (isFlatValueArray(current) && !Array.isArray(value)) {
        if (current.includes(value)) {
          if (current.length > 1) {
            // splice, not delete: an empty slot would reach the qq URL as undefined.
            current.splice(current.indexOf(value), 1);
          } else {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete filterBy.value[key];
          }
        } else {
          current[current.length] = value;
        }
      } else if (Array.isArray(value)) {
        // Range filter: value is [min, max], stored nested as [[min, max]] because the
        // RangeFilter consumers read it as filterBy[id][0][0] / [0][1].
        filterBy.value[key] = [value];
      } else {
        // Scalar value (a single id): store as a one-element flat list.
        filterBy.value[key] = [value];
      }
    });
  }

  function setActiveFilterSingleValue (filter: Record<string, FilterValue | FilterValue[]>) {
    Object.entries(filter).forEach(([key, value]) => {
      filterBy.value[key] = value;
    });
  }

  function setFilterFromQuery (query: { qq?: string }) {
    if (query.qq) {
      const p = parseQq(query.qq);
      filterBy.value = p.filterParams || {};
      sortKey.value = p.sortKey || null;
      sortBy.value = p.sortBy || null;
    } else {
      filterBy.value = {};
      sortKey.value = null;
      sortBy.value = null;
    }
  }

  function resetFilterBy () {
    filterBy.value = {};
  }

  // Only the single-level `subcategory` filter; the hierarchical one is cleared
  // through resetFilterByType('categories').
  function resetFilterBySubcategory () {
    delete filterBy.value.subcategory;
  }

  function resetFilterByType (key: string) {
    if (filterBy.value[key]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete filterBy.value[key];
    }
  }

  function setAlcoholProductIsLoading (payload: boolean) {
    alcoholProductIsLoading.value = payload;
  }

  function setVisitedPage (page: unknown) {
    visitedPages.value.push(page);
  }

  function setLastFilterableResource (resource: unknown) {
    lastFilterableResource.value = resource;
  }

  function saveProductListHistoryInformation ({
    productId,
    route,
  }: {
    productId: number | string;
    route: string;
  }) {
    productListHistoryInformation.value = {
      products: products.value,
      location: route,
      productId,
      currentPage: currentPage.value,
      returnEnabled: false,
    };
  }

  function enableProductListHistoryInformation () {
    if (productListHistoryInformation.value) {
      productListHistoryInformation.value.returnEnabled = true;
    }
  }

  function restoreFromProductListHistoryInformation () {
    if (!productListHistoryInformation.value) {
      return;
    }
    products.value = productListHistoryInformation.value.products;
    currentPage.value = productListHistoryInformation.value.currentPage;
  }

  function setFlagFilter (flagId: number | null) {
    flagFilter.value = flagId;
  }

  async function getResult (params: ProductQueryParams) {
    const {
      $api,
      $logger,
    } = nuxtApp;

    await authenticationStore.reInitToken();
    await awCacheKeyStore.ensureCacheSegmentation();

    function keyValueFlattener (obj: FilterParams): Array<[string, FilterValue]> {
      return Object.entries(obj).filter(([, v]) => {
        return v !== null;
      }).map(([k, v]): [string, Array<FilterValue | FilterValue[]>] => {
        return [k, Array.isArray(v) ? v : [v]];
      }).map(([k, v]) => {
        // flat() collapses the range filter's nested [[min, max]] to [min, max] here
        // (the backend takes each id as a separate filterValues entry).
        return v.flat().map((n): [string, FilterValue] => [k, n]);
      }).flat();
    }

    try {
      if (params?.isCached === undefined) {
        $logger.warn('getResult fallbacking to uncached content. To silence this warning specify the cache parameter.');
      }
      const isCacheRequested = Boolean(params?.isCached);
      const subCategoryId = firstFilterValue(params.filterParams?.subcategory);
      // `categories` travels on its own query parameter (`categories[]`), not through
      // the generic filters/filterValues pairs — the backend rejects it there.
      const categories = toIdArray(params.filterParams?.categories);
      const fp = keyValueFlattener(params.filterParams || {}).filter(([k]) => k !== 'subcategory' && k !== 'categories');
      return await $api.get<ProductsResponseData>(`/products${otqs({
        ...params,
        isCached: null,
        filterParams: null,
        filters: fp.map(([k]) => k),
        filterValues: fp.map(([, v]) => v),
        subCategoryId,
        categories: categories.length ? categories : null,
      })}`, { awAxCache: isCacheRequested ? AW_AX_CACHE_SEGMENTATION : undefined })
        .then((ax) => {
          const isHit = 'awAxCacheHit' in ax && Boolean(ax.awAxCacheHit);
          const data = ax.data;
          if (Array.isArray(data?.results)) {
            data.results.forEach((r) => {
              r.feMeta = { isCached: isHit };
              if (r.defaultVariant) {
                delete r.defaultVariant;
              }
            });
          }
          return {
            isCached: isHit,
            data,
          };
        });
    } catch (error) {
      if (!((params.listId || params.listName) && isApiError(error) && error?.response?.status === 404)) {
        $logger.error(error);
        setErrorNotification({ nuxtApp, error });
      }
    }
  }

  async function fetchCachedProductOrFallback ({ params }: { params: ProductQueryParams }) {
    try {
      const first = await getResult(params);
      if (first?.isCached && first.data?.containsAgeRestrictedProduct) {
        const second = await getResult({
          ...params,
          isCached: false,
        });
        return {
          isCached: Boolean(second?.isCached),
          value: second?.data,
        };
      }
      return {
        isCached: Boolean(first?.isCached),
        value: first?.data,
      };
    } catch {
      return {
        isCached: false,
        value: undefined,
      };
    }
  }

  async function fetchUnCachedPartsReqAndUpdateStore ({ productIds }: { productIds: Array<number | undefined> }) {
    const { $logger } = nuxtApp;
    const resp = await getUnCachedPartsReq({ productIds });
    if (resp?.list && Object.keys(resp.list).length) {
      for (const k in resp.list) {
        const entry = resp.list[k];
        if (entry) {
          resp.list[k] = new UnCachedProduct(entry);
        }
      }
      updateProdWithUnCachedData({
        productsToMutate: products.value,
        payload: resp.list,
        $logger,
      });
    }
  }

  async function getUnCachedPartsReq ({ productIds }: { productIds: Array<number | undefined> }) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      if (productIds?.length) {
        const result = await $api.$get<CartButtonsResponse>(`/products/cart_buttons_display_status${otqs({ productIds })}`);
        const mapped: Record<string, UnCachedProductData> = {};
        result.list.forEach((item) => {
          mapped[item.productId] = item;
        });
        return {
          ...result,
          list: mapped,
        };
      }
    } catch (err) {
      $logger.error(err);
    }
  }

  async function fetchSimilarProductFilters ({
    productId,
    categoryId,
  }: {
    productId: number | string;
    categoryId: number | string;
  }) {
    const {
      $api,
      $awt,
      $logger,
    } = nuxtApp;
    const pushSimilarError = () => {
      notificationStore.pushError({
        text: {
          title: $awt('awd.common.similar_products.empty'),
        },
      });
    };

    const generateProductFilters = (originFilters: SimilarProductFilter[]) => {
      const result: Record<string, string[]> = {};
      originFilters.forEach((currentFilter) => {
        if (currentFilter.parameter) {
          result[currentFilter.parameter] = currentFilter.values;
        }
      });
      return result;
    };

    try {
      const response = await $api.$get<SimilarProductFiltersResponse>(`similar_product_filters/${productId}/${categoryId}`);

      const productFilters = response.filters;

      if (!productFilters.length) {
        pushSimilarError();
        return;
      }

      const generatedFilters = generateProductFilters(productFilters);
      return JSON.stringify(Object.keys(generatedFilters || {}).length ? { filterParams: generatedFilters } : {});
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return null;
    }
  }

  async function fetchProducts (params: ProductQueryParams) {
    const {
      $awAnalytics,
      $awt,
      $cookies,
      $getHref,
      $logger,
    } = nuxtApp;

    try {
      const newQuery = JSON.stringify({
        ...(Object.keys(params.filterParams || {}).length ? { filterParams: params.filterParams } : null),
        ...(params.sortKey ? { sortKey: params.sortKey } : null),
        ...(params.sortBy ? { sortBy: params.sortBy } : null),
      });

      const {
        value: result,
        isCached,
      } = await fetchCachedProductOrFallback({ params });

      const uncachedReq = (isCached
        ? getUnCachedPartsReq({ productIds: (result?.results || []).map(p => p.id) })
        : Promise.resolve(undefined)
      );

      if (typeof window !== 'undefined') {
        const s = new URLSearchParams(window.location.search);
        if (newQuery !== '{}') {
          // Pass the raw JSON: URLSearchParams.toString() percent-encodes it, so
          // encoding here as well would put a second layer in the URL.
          s.set('qq', newQuery);
        } else {
          s.delete('qq');
        }
        const search = s.toString() ? `?${s.toString()}` : '';
        const newUrl = `${window.location.pathname}${search}`;
        if (newUrl !== (window.location.pathname + (window.location.search ? ('?' + new URLSearchParams(window.location.search)) : ''))) {
          await router.replace(newUrl);
        }
        useUserInterfaceStore().setRouteHistoryPathIfChanged({ fullPath: router.resolve(`${(window.location.pathname + (window.location.search ? ('?' + new URLSearchParams(window.location.search)) : ''))}`).href });
      }

      const subCategoryAnaliticsReq = (async () => {
        try {
          const newHref = $getHref();
          const urlResource = extractResourceFromPath(newHref);
          const subCategoryId = getSubcategoryId({ href: newHref });
          const categoryId = urlResource?.resourceType === 'category' ? urlResource.resourceId : null;
          const finalId = subCategoryId || categoryId;
          const seoResult = finalId ? await seoStore.getSeoData({
            seoModule: SEO_MODULE_CATEGORY,
            id: finalId,
          }) : null;
          if (seoResult) {
            $awAnalytics[PUSH_CATEGORY_VIEW]({ categoryName: seoResult.title ?? '' });
          }
        } catch (err) {
          $logger.error(err);
        }
      })();

      const tempObj = {
        containsAgeRestrictedProduct: result?.containsAgeRestrictedProduct,
        itemCount: result?.itemCount,
        sortValues: (result?.sortValues || []).filter(sv => sv.value !== 'default'),
        filters: result?.filters,
        sortKey: params?.sortKey,
        sortBy: params?.sortBy,
        itemsPerPage: itemsPerPage.value,
        currentPage: result?.currentPage,
        pageCount: result?.pageCount,
        noResultsFor: result?.noResultsFor,
        productsCategories: result?.categories,
      };

      if (result?.currentPage === 1) {
        products.value = result?.results || [];
      } else {
        pushProducts(result?.results || []);
      }

      setCategoryPageAttrs(tempObj);

      if (result?.containsAgeRestrictedProduct && modalStore.activeGenericModal.type !== M_DELIVERY_METHOD) {
        if (!modalStore.modals.genericModalQueue.some(m => hasAlcoholModalGuarantee(m?.item?.data))) {
          if (!userStore.isEcomUser) {
            commitAlcoholAlertModal({ $awt });
          } else if (!result.ageConfirmed && !$cookies.get('under18')) {
            commitAlcoholConfirmModal($awt);
          }
        }
      }

      const resp = await uncachedReq;
      if (resp?.list && Object.keys(resp.list).length) {
        for (const k in resp.list) {
          const entry = resp.list[k];
          if (entry) {
            resp.list[k] = new UnCachedProduct(entry);
          }
        }
        updateProdWithUnCachedData({
          productsToMutate: products.value,
          payload: resp.list,
          $logger,
        });
      }

      await subCategoryAnaliticsReq;

      return !!result;
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return false;
    }
  }

  function throttledRefresh () {
    if (!throttledRefreshObject.promise) {
      throttledRefreshObject.promise = new Promise((resolve) => {
        throttledRefreshObject.resolve = resolve;
      });
    }
    if (throttledRefreshObject.id) {
      clearTimeout(throttledRefreshObject.id);
    }
    throttledRefreshObject.id = setTimeout(() => {
      refreshProducts.value++;
      throttledRefreshObject.promise = null;
      throttledRefreshObject.resolve?.();
    }, 400);
    return throttledRefreshObject.promise;
  }

  function setFilterKeyword (key: string, payload: unknown) {
    filterKeyword.value[key] = payload;
  }

  function setupProductList () {
    const { $logger } = nuxtApp;
    if (import.meta.server) {
      $logger.warn('setupProductList should not run on server side');
      return;
    }
    itemsPerPage.value = getItemsPerPage.value;
    if (!(productListHistoryInformation.value?.returnEnabled)) {
      products.value = products.value.slice(0, itemsPerPage.value ?? undefined);
    }
  }

  const getCategorySelectionFilter = computed(() => {
    return filters.value?.find(filter => filter.selectionType === 'categorySelection');
  });
  const getItemsPerPage = computed(() => {
    const userInterfaceStore = useUserInterfaceStore();
    if (userInterfaceStore.mediaQueries['desktop-large-min']) {
      return 16;
    }
    if (userInterfaceStore.mediaQueries['desktop-small-min']) {
      return 12;
    }
    return 8;
  });
  const highlightedProductCount = computed(() => {
    const isHighlighted = (productData: Product) => {
      return !!productData?.highlightedParameters?.length || false;
    };
    const idxOfFirstNonHighlighted = products.value.findIndex(productData => !isHighlighted(productData));
    return idxOfFirstNonHighlighted === -1 ? products.value.length : idxOfFirstNonHighlighted;
  });
  const renderedProducts = computed(() => {
    // todo add comment for what happens here
    // If itemsPerPage is 12 use MAGIC
    const difference = getItemsPerPage.value === 12
      ? Math.max(products.value.length - (Math.floor(highlightedProductCount.value / 2) + Math.ceil(highlightedProductCount.value % 2 * 0.5)), getItemsPerPage.value / 2 * 4 / 3)
      : products.value.length - Math.min(highlightedProductCount.value, getItemsPerPage.value / 2);

    return currentPage.value === pageCount.value ? products.value : products.value.slice(0, difference);
  });

  return {
    products,
    productsCategories,
    containsAgeRestrictedProduct,
    currentPage,
    itemCount,
    pageCount,
    itemsPerPage,
    sortValues,
    filters,
    sortKey,
    sortBy,
    filterBy,
    searchText,
    noResultsFor,
    productListHistoryInformation,
    refreshProducts,
    alcoholProductIsLoading,
    visitedPages,
    lastFilterableResource,
    filterKeyword,
    filterPanelVisible,
    pushProducts,
    setCategoryPageAttrs,
    setSearchText,
    clearFilterAndSort,
    setActiveFilter,
    setActiveFilterSingleValue,
    setFilterFromQuery,
    resetFilterBy,
    resetFilterBySubcategory,
    resetFilterByType,
    setAlcoholProductIsLoading,
    setVisitedPage,
    setLastFilterableResource,
    saveProductListHistoryInformation,
    enableProductListHistoryInformation,
    restoreFromProductListHistoryInformation,
    getResult,
    fetchCachedProductOrFallback,
    fetchUnCachedPartsReqAndUpdateStore,
    getUnCachedPartsReq,
    fetchSimilarProductFilters,
    fetchProducts,
    throttledRefresh,
    setFilterKeyword,
    setupProductList,
    getCategorySelectionFilter,
    getItemsPerPage,
    renderedProducts,
    highlightedProductCount,
    flagFilter,
    setFlagFilter,
  };
});

// A flat list of scalar ids (categories/brand), as opposed to a scalar or the
// range filter's nested [[min, max]].
function isFlatValueArray (v: FilterValueDeep | undefined): v is FilterValue[] {
  return Array.isArray(v) && !Array.isArray(v[0]);
}

function firstFilterValue (v: FilterValueDeep | undefined): FilterValue | null {
  if (Array.isArray(v)) {
    const first = v[0];
    return (Array.isArray(first) ? first[0] : first) ?? null;
  }
  return v ?? null;
}

interface QqPayload {
  filterParams?: FilterParams;
  sortKey?: string | null;
  sortBy?: string | null;
}

function isQqPayload (v: unknown): v is QqPayload {
  return typeof v === 'object' && v !== null;
}

// The router hands over `query.qq` already decoded once, which leaves plain JSON.
// Bookmarked and shared links may carry an extra percent-encoded layer (`%7B…`)
// that is not valid JSON, so fall back to one more decodeURIComponent for those.
// An unparseable value yields no filters rather than throwing on page load.
function parseQq (qq: string): QqPayload {
  const attempts = [qq, decodeURIComponent(qq)];
  for (const attempt of attempts) {
    try {
      const parsed: unknown = JSON.parse(attempt);
      if (isQqPayload(parsed)) {
        return parsed;
      }
    } catch {
      // Try the next form.
    }
  }
  return {};
}

// Values come back from the `qq` URL as strings, the endpoint expects numeric ids.
// Non-numeric entries are dropped: the backend ignores them and returns the
// *unfiltered* list, which would look like a filter that lost its criteria.
function toIdArray (v: FilterValueDeep | undefined): number[] {
  if (v === undefined || v === null) {
    return [];
  }
  return [v].flat().map(Number).filter(Number.isFinite);
}

function hasAlcoholModalGuarantee (data: unknown): boolean {
  return typeof data === 'object' && data !== null && 'alcoholModalUniquenessGuarantee' in data && Boolean(data.alcoholModalUniquenessGuarantee);
}
